import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { toast } from "sonner";
import axios from "axios";
import { API_URL } from "../lib/utils";
import { 
  Building2, Phone, MapPin, FileText, Mail, ChefHat, Sun, Moon, LogOut
} from "lucide-react";

const CompanySetupPage = () => {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    nif: "",
    address: "",
    city: "Luanda",
    postal_code: "0000",
    country: "AO",
    phone: "",
    email: "",
  });

  const { user, token, refreshUser, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.name.trim()) {
      toast.error("O nome da empresa é obrigatório");
      return;
    }
    
    if (!form.nif.trim()) {
      toast.error("O NIF da empresa é obrigatório");
      return;
    }

    setLoading(true);

    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API_URL}/auth/complete-company-setup`, form, {
        headers,
        withCredentials: true,
      });
      
      toast.success("Empresa criada com sucesso!");
      
      // Atualizar dados do utilizador
      if (refreshUser) {
        await refreshUser();
      }
      
      navigate("/dashboard", { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao criar empresa");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-background">
      <div className="w-full max-w-lg space-y-8">
        {/* Theme Toggle */}
        <div className="flex justify-end">
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleTheme}
            className="rounded-full"
          >
            {theme === "dark" ? (
              <Sun className="h-5 w-5" />
            ) : (
              <Moon className="h-5 w-5" />
            )}
          </Button>
        </div>

        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="p-3 rounded-xl bg-primary">
            <ChefHat className="w-8 h-8 text-primary-foreground" />
          </div>
          <h1 className="text-3xl font-bold font-['Outfit']">Lumora</h1>
        </div>

        <Card className="border-0 shadow-xl bg-card">
          <CardHeader className="space-y-1 text-center">
            <CardTitle className="text-2xl font-bold font-['Outfit']">
              Configure a sua Empresa
            </CardTitle>
            <CardDescription>
              Olá <strong>{user?.name}</strong>! Complete o registo da sua empresa para continuar.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-sm">
                <p className="text-emerald-600 dark:text-emerald-400 font-medium">
                  Você será o <strong>Administrador</strong> desta empresa e poderá criar funcionários depois.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="company-name">Nome da Empresa *</Label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="company-name"
                    type="text"
                    placeholder="Restaurante Bom Sabor"
                    className="pl-10 h-12"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    required
                    data-testid="company-name-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="company-nif">NIF da Empresa *</Label>
                <div className="relative">
                  <FileText className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="company-nif"
                    type="text"
                    placeholder="123456789"
                    className="pl-10 h-12"
                    value={form.nif}
                    onChange={(e) => setForm({ ...form, nif: e.target.value })}
                    required
                    data-testid="company-nif-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="company-address">Morada</Label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="company-address"
                    type="text"
                    placeholder="Rua da Missão, 123, Maianga, Luanda"
                    className="pl-10 h-12"
                    value={form.address}
                    onChange={(e) => setForm({ ...form, address: e.target.value })}
                    data-testid="company-address-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="company-phone">Telefone</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="company-phone"
                      type="tel"
                      placeholder="+244 923..."
                      className="pl-10 h-12"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      data-testid="company-phone-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="company-email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="company-email"
                      type="email"
                      placeholder="empresa@email.com"
                      className="pl-10 h-12"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      data-testid="company-email-input"
                    />
                  </div>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-12 text-base font-semibold"
                disabled={loading}
                data-testid="submit-company-btn"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    A criar empresa...
                  </span>
                ) : (
                  "Criar Empresa e Continuar"
                )}
              </Button>
            </form>

            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={async () => { await logout(); navigate("/login", { replace: true }); }}
                className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary hover:underline"
                data-testid="setup-logout-btn"
              >
                <LogOut className="w-4 h-4" />
                Sair e entrar com outra conta
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CompanySetupPage;
